import { UploadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Spin, Upload } from "antd";
import { UploadProps } from "antd/es/upload";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import { useImportServiceTickets } from "~/services/service-ticket";

export const ImportServiceTicketModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const notification = useNotification();
  const importServiceTicketsMutation = useImportServiceTickets();

  const handleUpload: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
  }) => {
    // Validate file
    if (!(file instanceof File) || file.type !== "text/csv") {
      notification.error({
        message: "Invalid file type",
        description: "Please upload a valid CSV file.",
      });
      onError?.(new Error("Invalid file type"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notification.error({
        message: "File too large",
        description: "The CSV file must be smaller than 10MB.",
      });
      onError?.(new Error("File too large"));
      return;
    }

    setIsUploading(true);

    importServiceTicketsMutation.mutate(file, {
      onSuccess: (response) => {
        const { total, created, failed, errors } = response;
        if (created > 0) {
          notification.success({
            message: "Service tickets imported successfully!",
            description: `Successfully imported ${created} of ${total} service tickets.`,
          });
          queryClient.invalidateQueries({
            queryKey: ["bookings", "list"],
          });
          setOpen(false);
        }
        if (failed > 0) {
          notification.warning({
            message: "Some service tickets failed to import",
            description: `${failed} service tickets could not be imported. Check the console for details.`,
          });
          console.error("Import errors:", errors);
        }
        onSuccess?.({});
      },
      onError: (err: any) => {
        notification.error({
          message: "Error importing service tickets",
          description:
            err.message || "There was an error processing the CSV file.",
        });
        onError?.(err);
      },
      onSettled: () => {
        setIsUploading(false);
      },
    });
  };

  return (
    <>
      <Button
        type="primary"
        onClick={() => setOpen(true)}
        disabled={isUploading}
      >
        <UploadOutlined />
        <span>Import Service CSV</span>
      </Button>
      <Modal
        open={open}
        title="Import Service Tickets from CSV"
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={isUploading}>
          <Upload
            accept=".csv"
            customRequest={handleUpload}
            showUploadList={false}
            disabled={isUploading}
          >
            <Button icon={<UploadOutlined />} disabled={isUploading}>
              Click to Upload CSV
            </Button>
          </Upload>
          <div className="mt-4 text-sm text-gray-500">
            <p>Please upload a CSV file with the correct format.</p>
            <p>Maximum file size: 10MB.</p>
          </div>
        </Spin>
      </Modal>
    </>
  );
};
